// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertTriangleLine from "@store/Library/Components/Toolkit/Icons/AlertTriangle/AlertTriangleLine";
import InfoFill from "@store/Library/Components/Toolkit/Icons/Info/InfoFill";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { DataOption, DataOptions } from "@toolkit/Framework/Common/Utilities/dataOptions";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, {
    forwardRef,
    ForwardRefRenderFunction,
    HTMLAttributes,
    PropsWithChildren,
    ReactNode,
    useMemo,
} from "react";
import { css } from "styled-components";

export type AlertBoxTypeOption = DataOption;
export interface AlertBoxTypeOptionMapper {
    success: AlertBoxTypeOption;
    warning: AlertBoxTypeOption;
    error: AlertBoxTypeOption;
}
export type AlertBoxTypeOptions = DataOptions<AlertBoxTypeOption> & AlertBoxTypeOptionMapper;
export type AlertBoxType = keyof AlertBoxTypeOptionMapper;

export const alertBoxTypeOptions: AlertBoxTypeOptions = {
    success: { id: "success", displayName: "Success", isDefault: true },
    warning: { id: "warning", displayName: "Warning" },
    error: { id: "error", displayName: "Error" },
};

export interface AlertBoxPresentationProps extends ExpandStyles<AlertBoxStyles> {
    /** Visual status variant controlling alert colors. */
    type?: AlertBoxType;
    /** Forces title and secondary text onto separate lines when enabled. */
    stackTitleAndSecondaryText?: boolean;
    /** Shows the leading alert icon. */
    showIcon?: boolean;
}

export interface AlertBoxProps extends AlertBoxPresentationProps, Omit<HTMLAttributes<HTMLElement>, "title"> {
    /** Primary message content. */
    title?: ReactNode;
    /** Supporting message content. */
    secondaryText?: ReactNode;
    /** Tooltip message displayed by the trailing info trigger. */
    tooltipText?: string;
}

export interface AlertBoxStyles {
    padding?: string;
    color?: string;
    backgroundColor?: string;
    borderRadius?: string;
    isCentered?: boolean;
    wrapper?: InjectableCss;
    icon?: IconPresentationProps;
    content?: InjectableCss;
    textGroup?: InjectableCss;
    title?: TypographyPresentationProps;
    secondaryText?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const alertBoxStyles: AlertBoxStyles = {
    padding: pxToRem(8),
    borderRadius: pxToRem(4),
    wrapper: {
        css: css`
            ${spacingStyles()}
            display: flex;
            gap: ${pxToRem(8)};
            align-items: flex-start;
            width: 100%;
        `,
    },
    content: {
        css: css`
            display: flex;
            flex-grow: 1;
            flex-flow: row wrap;
            align-items: center;
            gap: ${pxToRem(8)};
            margin-top: ${pxToRem(1)};
        `,
    },
    textGroup: {
        css: css`
            min-width: 0;
        `,
    },
    icon: {
        src: AlertTriangleLine,
        size: 24,
        color: "currentColor",
        css: css`
            flex-shrink: 0;
        `,
    },
    tooltip: {
        iconProps: {
            src: InfoFill,
            size: 24,
            color: "text",
        },
        mergeCss: true,
        cssOverrides: {
            tooltipWrapper: css`
                flex-shrink: 0;
                margin: 0;
            `,
        },
    },
    title: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    secondaryText: {
        italic: true,
    },
};

export const alertBoxTypes: Partial<Record<AlertBoxType, AlertBoxStyles>> = {
    success: {
        color: "success",
        backgroundColor: "success.lighter",
    },
    warning: {
        color: "warning",
        backgroundColor: "warning.lighter",
    },
    error: {
        color: "danger",
        backgroundColor: "danger.lighter",
    },
};

const AlertBox: ForwardRefRenderFunction<HTMLDivElement, PropsWithChildren<AlertBoxProps>> = (
    {
        type = "success",
        title,
        secondaryText,
        stackTitleAndSecondaryText,
        showIcon = true,
        tooltipText,
        children,
        extendedStyles,
        overridedStyles,
        ...htmlAttributes
    },
    ref,
) => {
    const stylesAndDefinitions = useMergeProps(alertBoxStyles, alertBoxTypes[type]);
    const baseStyles = useExpandProps(stylesAndDefinitions, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        return mergeProps<AlertBoxStyles>(baseStyles, {
            wrapper: {
                css: css`
                    ${({ theme }) => css`
                        ${cssProp("padding", baseStyles.padding)}
                        ${cssProp("color", baseStyles.color, x => resolveColor(x, theme))}
                        ${cssProp("background-color", baseStyles.backgroundColor, x => resolveColor(x, theme))}
                        ${cssProp("border-radius", baseStyles.borderRadius)}
                    `}
                    ${() => {
                        if (baseStyles.isCentered) {
                            return css`
                                justify-content: center;
                            `;
                        }
                    }}
                `,
            },
            content: {
                css: css`
                    ${() => {
                        if (baseStyles.isCentered) {
                            return css`
                                flex-grow: 0;
                            `;
                        }
                    }}
                `,
            },
            title: {
                color: baseStyles.color,
            },
            secondaryText: {
                color: baseStyles.color,
            },
        });
    }, [baseStyles]);

    if (!title && !secondaryText && !children) {
        return null;
    }

    const hasTitle = Boolean(title);
    const hasSecondaryText = Boolean(secondaryText);
    const textElementTag = stackTitleAndSecondaryText ? "div" : "span";

    return (
        <StyledWrapper {...styles.wrapper} {...htmlAttributes} ref={ref}>
            {showIcon && <Icon {...styles.icon} />}
            <StyledWrapper {...styles.content}>
                {(hasTitle || hasSecondaryText) && (
                    <StyledWrapper {...styles.textGroup}>
                        {hasTitle && (
                            <Typography as={textElementTag} {...styles.title}>
                                {title}
                            </Typography>
                        )}
                        {!stackTitleAndSecondaryText && hasTitle && hasSecondaryText && <>&nbsp;&nbsp;</>}
                        {hasSecondaryText && (
                            <Typography as={textElementTag} {...styles.secondaryText}>
                                {secondaryText}
                            </Typography>
                        )}
                    </StyledWrapper>
                )}
                {children}
            </StyledWrapper>
            {!!tooltipText?.trim() && <Tooltip {...styles.tooltip} text={tooltipText} />}
        </StyledWrapper>
    );
};

export default forwardRef(AlertBox);
